// Universal <Avatar /> — renders the image if the entity has one, or
// initials on a deterministic colour otherwise (Gmail-style). Used
// everywhere a display_identity appears: bylines, dropdowns, dashboards,
// masthead.

interface Props {
    src?: string | null;
    name: string;
    size?: number;         // pixels, default 32
    /** Set a stable seed (e.g. user_id) so background colour doesn't
        change when display_name is edited. Falls back to name. */
    seed?: string;
    /** True for anon identities — renders a "?" instead of initials. */
    anonymous?: boolean;
    className?: string;
}

const PALETTE = [
    '#2b5e3f', '#7a2b5e', '#5e2b2b', '#2b3d5e',
    '#5e4a2b', '#3d5e2b', '#2b5e5e', '#4a2b5e',
    '#8b4513', '#2c5f2d', '#97572b', '#3e3e5e',
];

function pickColor(key: string): string {
    let h = 0;
    for (let i = 0; i < key.length; i++) {
        h = (h << 5) - h + key.charCodeAt(i);
        h |= 0;
    }
    return PALETTE[Math.abs(h) % PALETTE.length];
}

function initials(name: string): string {
    const parts = name.trim().split(/\s+/).slice(0, 2);
    if (parts.length === 0) return '?';
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[1][0]).toUpperCase();
}

export function Avatar({ src, name, size = 32, seed, anonymous, className = '' }: Props) {
    const style: React.CSSProperties = {
        width:  size + 'px',
        height: size + 'px',
        borderRadius: '50%',
        display: 'inline-flex',
        alignItems: 'center', justifyContent: 'center',
        overflow: 'hidden',
        verticalAlign: 'middle',
        flexShrink: 0,
    };
    if (src) {
        return (
            <img
                className={'avatar ' + className}
                src={src}
                alt={name}
                width={size}
                height={size}
                style={{ ...style, objectFit: 'cover' }}
                loading="lazy"
            />
        );
    }
    const bg = anonymous ? '#666' : pickColor(seed || name);
    const label = anonymous ? '?' : initials(name);
    return (
        <span
            className={'avatar avatar-placeholder ' + className}
            style={{
                ...style,
                background: bg,
                color: 'white',
                fontFamily: '-apple-system, sans-serif',
                fontWeight: 700,
                fontSize: Math.round(size * 0.42) + 'px',
                letterSpacing: '0.5px',
            }}
            title={name}
        >{label}</span>
    );
}
